import template from './jga-revocation-period-start.html.twig';
import './jga-revocation-period-start.scss';

const { Component } = Shopware;
const { Criteria } = Shopware.Data;

const DELIVERY_STATE_PREFIX = 'delivery_state:';

/**
 * Replacement for the plain single-select behind the "Revocation period starts
 * from" config field. Keeps the three fixed options (order / shipping / invoice
 * date) and additionally lists every order_delivery status of the shop —
 * including shop-specific custom states such as "Delivered" — grouped under a
 * header so the merchant can tell the fixed dates from the delivery statuses.
 *
 * Delivery statuses are stored as "delivery_state:<technicalName>"; the backend
 * (RevocationController) resolves the transition date from state_machine_history.
 */
Component.register('jga-revocation-period-start', {
    template,

    inject: ['repositoryFactory'],

    props: {
        value: {
            type: String,
            required: false,
            default: null,
        },
        label: {
            type: String,
            required: false,
            default: null,
        },
        helpText: {
            type: String,
            required: false,
            default: null,
        },
    },

    emits: ['update:value', 'change'],

    data() {
        return {
            deliveryStates: [],
        };
    },

    created() {
        this.loadDeliveryStates();
    },

    computed: {
        stateMachineStateRepository() {
            return this.repositoryFactory.create('state_machine_state');
        },

        // Mirror the backend fallback (?: 'shippingDate') so a brand-new shop with
        // no stored value shows "Shipping date" pre-selected — exactly like the
        // previous single-select default did via <defaultValue>.
        currentValue() {
            return this.value || 'shippingDate';
        },

        fixedOptions() {
            return [
                { value: 'orderDate', label: this.$tc('jga-revocation.periodStart.orderDate') },
                { value: 'shippingDate', label: this.$tc('jga-revocation.periodStart.shippingDate') },
                { value: 'invoiceDate', label: this.$tc('jga-revocation.periodStart.invoiceDate') },
            ];
        },

        deliveryStateOptions() {
            return this.deliveryStates.map((state) => ({
                value: DELIVERY_STATE_PREFIX + state.technicalName,
                label: this.stateLabel(state),
            }));
        },

        // Flat option list for sw-single-select. Group headers are non-selectable
        // rows (isHeader) rendered specially in the result-item slot.
        options() {
            const opts = [
                {
                    value: '__header_fixed',
                    label: this.$tc('jga-revocation.periodStart.groupFixed'),
                    // Explained via a help tooltip — unlike the delivery statuses,
                    // the fixed dates are not self-explanatory.
                    helpText: this.$tc('jga-revocation.periodStart.groupFixedHelp'),
                    isHeader: true,
                    disabled: true,
                },
                ...this.fixedOptions,
                {
                    value: '__header_delivery',
                    label: this.$tc('jga-revocation.periodStart.groupDelivery'),
                    helpText: this.$tc('jga-revocation.periodStart.groupDeliveryHelp'),
                    isHeader: true,
                    disabled: true,
                },
                ...this.deliveryStateOptions,
            ];

            // Keep the stored value representable even if its custom delivery status
            // was deleted meanwhile — otherwise the select would render empty and the
            // merchant could not tell what is configured.
            if (this.value && !opts.some((option) => option.value === this.value)) {
                opts.push({ value: this.value, label: this.unknownLabel(this.value) });
            }

            return opts;
        },
    },

    methods: {
        async loadDeliveryStates() {
            try {
                const criteria = new Criteria(1, 100);
                criteria.addFilter(Criteria.equals('stateMachine.technicalName', 'order_delivery.state'));
                criteria.addSorting(Criteria.sort('name', 'ASC'));
                const result = await this.stateMachineStateRepository.search(criteria, Shopware.Context.api);
                this.deliveryStates = [...result];
            } catch (e) {
                this.deliveryStates = [];
            }
        },

        stateLabel(state) {
            // Prefer the merchant-maintained / shipped translation, fall back to the
            // technical name for custom states without a translation in this locale.
            return state.translated?.name || state.name || state.technicalName;
        },

        unknownLabel(value) {
            const technicalName = value.startsWith(DELIVERY_STATE_PREFIX)
                ? value.slice(DELIVERY_STATE_PREFIX.length)
                : value;
            return this.$tc('jga-revocation.periodStart.unknownState', 0, { name: technicalName });
        },

        onChange(value) {
            // Never persist a group-header pseudo row.
            if (!value || value.startsWith('__header_')) {
                return;
            }
            this.$emit('update:value', value);
            this.$emit('change', value);
        },
    },
});
